/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';

import { GlobalStyle } from 'styles/global-styles';
import 'react-toastify/dist/ReactToastify.css';

import { NotFoundPage } from './components/NotFoundPage';
import PrivateRoute from 'app/containers/PrivateRoute';
import ManagerRoute from 'app/containers/ManagerRoute';
import PinProtectedRoute from 'app/containers/PinProtectedRoute';
import { ToursPage } from './pages/ToursPage';
import { LoginPage } from './pages/LoginPage';
import { PasswordResetRequestPage } from './pages/PasswordResetRequestPage';
import { PasswordResetConfirmPage } from './pages/PasswordResetConfirmPage';
import { AuthProvider } from 'app/containers/AuthProvider';
import { ConfirmationPage } from 'app/pages/ConfirmationPage';
import { ToastContainer } from 'react-toastify';
import { ProductionPage } from 'app/pages/ProductionPage';
import { BasePickingListPage } from 'app/pages/ProductionPage/BasePickingListPage';
import BasePickingDetailsPage from 'app/pages/ProductionPage/BasePickingListPage/BasePickingDetailsPage';
import CuttingPanePage from 'app/pages/ProductionPage/CuttingPanePage';
import CornersPickingPage from 'app/pages/ProductionPage/CornersPickingPage';
import DrawersPage from 'app/pages/ProductionPage/DrawersPage';
import DrawerPickingPage from 'app/pages/ProductionPage/DrawersPage/DrawerPickingPage';
import DrawersCuttingPage from 'app/pages/ProductionPage/DrawersCuttingPage';
import WaypointsPage from 'app/pages/WaypointsPage';
import DriverPage from 'app/pages/DriverPage';
import AddWaypointPage from 'app/pages/WaypointsPage/AddWaypoint';
import PickupPage from 'app/containers/PickupPage';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from 'styles/theme';
import HistoryPage from 'app/pages/HistoryPage';
import FleetTrackingPage from 'app/pages/FleetTrackingPage';
import PackagesQRCodesPage from './pages/PackagesQRCodesPage';
import TourPackagesQRCodesPage from './pages/TourPackagesQRCodesPage';
import PickingPiecesQRCodesPage from './pages/PickingPiecesQRCodesPage';
import TourCarrierDeliveryInfosPage from './pages/TourCarrierDeliveryInfosPage';
import CarrierCombinedPage from 'app/pages/CarrierCombinedPage';
import PrintingPage from 'app/pages/PrintingPage';
import AboutModal from 'app/components/AboutModal';
import AutoUpdatePrompt from 'app/components/AutoUpdatePrompt';

export function App() {
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const [aboutVersion, setAboutVersion] = React.useState('');

  React.useEffect(() => {
    let isMounted = true;
    const getVersion = window.electronAPI?.app?.getVersion;
    if (typeof getVersion === 'function') {
      getVersion()
        .then(version => {
          if (isMounted && typeof version === 'string') {
            setAboutVersion(version);
          }
        })
        .catch(() => {
          /* ignore */
        });
    }
    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    const unsubscribe =
      window.electronAPI?.app?.onShowAbout?.(({ version }) => {
        if (typeof version === 'string' && version.length > 0) {
          setAboutVersion(version);
        }
        setAboutOpen(true);
      }) ?? undefined;

    return unsubscribe;
  }, []);

  const handleCloseAbout = React.useCallback(() => {
    setAboutOpen(false);
  }, []);

  return (
    <>
      <Helmet titleTemplate="%s - Expedinet" defaultTitle="Expedinet">
        <meta name="description" content="Logistics tools for Kewlox" />
      </Helmet>

      <AuthProvider>
        <ThemeProvider theme={theme}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/password-reset"
              element={<PasswordResetRequestPage />}
            />
            <Route
              path="/reset-password"
              element={<PasswordResetConfirmPage />}
            />
            <Route path="/c/:token" element={<ConfirmationPage />} />
            <Route path="/pi/:orderId/:pickupCode" element={<PickupPage />} />

            <Route element={<PrivateRoute />}>
              <Route path="/" element={<ToursPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/waypoint" element={<WaypointsPage />} />
              <Route path="/waypoint/add" element={<AddWaypointPage />} />
              <Route
                path="/waypoint/edit/:waypointId"
                element={<AddWaypointPage />}
              />
              <Route path="/driver" element={<DriverPage />} />
              <Route path="/driver" element={<DriverPage />} />
              <Route
                path="/carrier-management"
                element={<CarrierCombinedPage />}
              />
              <Route
                path="/carrier-management/:id"
                element={<CarrierCombinedPage />}
              />
              <Route path="/production" element={<ProductionPage />} />
              <Route
                path="/production/picking/1"
                element={<CornersPickingPage />}
              />
              <Route
                path="/production/picking/:workstation"
                element={<BasePickingListPage />}
              />
              <Route
                path="/production/picking/:workstation/:orderId"
                element={<BasePickingDetailsPage />}
              />
              <Route path="/production/cutting" element={<CuttingPanePage />} />
              <Route path="/production/drawers" element={<DrawersPage />} />
              <Route
                path="/production/drawers-cutting"
                element={<DrawersCuttingPage />}
              />
              <Route
                path="/production/drawers/picking/:productId"
                element={<DrawerPickingPage />}
              />
              <Route
                path="/order/:orderId/packages/qrcodes"
                element={<PackagesQRCodesPage />}
              />
              {import.meta.env.DEV && (
                <Route
                  path="/order/:orderId/picking/qrcodes"
                  element={<PickingPiecesQRCodesPage />}
                />
              )}
              <Route
                path="/tour/:tourId/packages/qrcodes"
                element={<TourPackagesQRCodesPage />}
              />
              <Route
                path="/tour/:tourId/carrier-delivery-infos"
                element={<TourCarrierDeliveryInfosPage />}
              />
              <Route path="/printing" element={<PrintingPage />} />

              {/* Routes reserved for managers with PIN protection */}
              <Route element={<ManagerRoute />}>
                <Route
                  path="/fleet-tracking"
                  element={
                    <PinProtectedRoute>
                      <FleetTrackingPage />
                    </PinProtectedRoute>
                  }
                />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ThemeProvider>
      </AuthProvider>
      <GlobalStyle />
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
      rtl={false}
      draggable={false}
      pauseOnHover={false}
    />
      <AutoUpdatePrompt />
      <AboutModal open={aboutOpen} onClose={handleCloseAbout} version={aboutVersion} />
    </>
  );
}
